import React from "react";
import { Tab, Tabs } from "@mui/material";
import PropTypes from "prop-types";

import BottomBar from "../../../ui/BottomBar";
import StepContainer from "../../../ui/StepContainer";
import StepForm from "../../../ui/StepForm";
import {
  DEMAND_PROFILES_PARAM_KEYS,
  STEP_STORAGE_KEYS,
  TRAFFIC_VOLUME_KEYS,
} from "../../../constants/storageConstants";
import DemandProfileForm from "./DemandProfileForm";
import {
  getHourlyProfileTotal,
  PARAM_KEY_TO_PROFILE_TYPE,
  PROFILES_WITH_SPECIFIC_TOTALS,
} from "./defaultProfileParamsConstants";
import { projectDataPropType } from "../../../util/propTypeHelper";
import Header from "../../../ui/Header";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "../../../constants/routesConstants";

export default function DemandProfiles({ projectData, saveFormData }) {
  const navigate = useNavigate();
  const handleButtonClick = () => navigate(ROUTES.TRAFFIC_VOLUMES);
  const [currentTab, setCurrentTab] = React.useState(0);

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const getA11yProps = (index) => ({
    id: `tab-${index}`,
    "aria-controls": `tabpanel-${index}`,
  });
  const facilityType =
    projectData[STEP_STORAGE_KEYS.GLOBAL_INPUT_DATA]?.facilityType;
  return (
    <StepContainer withProgressStepper={false}>
      <StepForm
        onFormSubmit={handleButtonClick}
        projectData={projectData}
        saveFormData={saveFormData}
        storageKey={`${STEP_STORAGE_KEYS.TRAFFIC_VOLUMES}.${TRAFFIC_VOLUME_KEYS.DEFAULT_DEMAND_PROFILES}`}
      >
        {({
          clearErrors,
          defaultValues,
          errors,
          formData,
          register,
          setError,
        }) => {
          const paramKey = Object.values(DEMAND_PROFILES_PARAM_KEYS)[
            currentTab
          ];
          const total = PROFILES_WITH_SPECIFIC_TOTALS.includes(paramKey)
            ? getHourlyProfileTotal({ facilityType, formData, paramKey })
            : null;
          const isTotalValid =
            total === null ? null : Math.abs(total - 100) < 0.001; // the total should be within 0.001 of 100%
          const getDemandProfileFormProps = ({ paramKey }) => {
            return {
              clearErrors,
              defaultValues: defaultValues?.[paramKey],
              errors,
              facilityType,
              formData: formData?.[paramKey],
              isTotalValid,
              paramKey,
              profilesType: PARAM_KEY_TO_PROFILE_TYPE[paramKey],
              register,
              setError,
              total,
            };
          };
          const rootLevelErrorMessage =
            errors.root?.[DEMAND_PROFILES_PARAM_KEYS.HOURLY_WEEKDAY]?.message ||
            errors.root?.[DEMAND_PROFILES_PARAM_KEYS.HOURLY_WEEKEND]?.message;

          return (
            <>
              <Header
                subtitle="Contains the demand profile used for the life cycle cost analysis.  Default profiles can be overridden."
                title="Detailed Demand Profiles"
              />
              <Tabs
                aria-label="tabs for updating detailed demand profiles"
                onChange={handleTabChange}
                sx={{
                  borderBottom: "1px solid #B1B1B1",
                  marginBottom: "30px",
                  marginTop: "40px",
                  width: "100%",
                }}
                value={currentTab}
              >
                <Tab label="Daily" {...getA11yProps(0)} />
                <Tab label="Monthly" {...getA11yProps(1)} />
                <Tab label="Hourly - Weekday" {...getA11yProps(2)} />
                <Tab label="Hourly - Weekend" {...getA11yProps(3)} />
              </Tabs>
              {Object.values(DEMAND_PROFILES_PARAM_KEYS).map(
                (paramKey, index) => {
                  return (
                    currentTab === index && (
                      <DemandProfileForm
                        key={paramKey}
                        {...getDemandProfileFormProps({ paramKey })}
                      />
                    )
                  );
                }
              )}
              <BottomBar
                handleSecondaryButtonClick={handleButtonClick}
                isFullScreenWidth
                mainButtons={[
                  {
                    isDisabled: !!rootLevelErrorMessage,
                    label: "Save",
                    tooltipText: rootLevelErrorMessage,
                  },
                ]}
                projectData={projectData}
                secondaryButtonLabel="Cancel"
                withSubmitButton
              />
            </>
          );
        }}
      </StepForm>
    </StepContainer>
  );
}

DemandProfiles.propTypes = {
  projectData: projectDataPropType.isRequired,
  saveFormData: PropTypes.func.isRequired,
};
